import React, { useState, useEffect } from "react";
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import apiClient from "../../utils/apiClient";

const DataTable = () => {
    const [openModal, setOpenModal] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch banners from API
    useEffect(() => {
        const fetchBanners = async () => {
            setLoading(true);
            try {
                const response = await apiClient.get('/banner/get');
                setBanners(response.data.banners); 
            } catch (err) {
                setError('Failed to fetch banners');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchBanners();
    }, []);

    const handleOpenModal = () => setOpenModal(true);

    const handleCloseModal = () => {
        setOpenModal(false);
        setImagePreview(null);
        setUploadedFile(null);
    };

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const imageURL = URL.createObjectURL(file);
            setImagePreview(imageURL);
            setUploadedFile(file);
        }
    };

    const removeImage = () => {
        setImagePreview(null);
        setUploadedFile(null);
    };

    const handleSubmit = async () => {
        if (uploadedFile) {
            setLoading(true);
            try {
                // Step 1: Upload the image to /shared/upload
                const formData = new FormData();
                formData.append('image', uploadedFile);

                const uploadResponse = await apiClient.post('/shared/upload', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                // Step 2: Call /banner/add with the uploaded image URL
                const imageUrl = uploadResponse.data.imageUrl; // Assuming the response contains the image URL
                await apiClient.post('/banner/add', { image: imageUrl });

                // Refresh banners list after successful upload
                const response = await apiClient.get('/banner/get');
                setBanners(response.data.banners);

                handleCloseModal();
            } catch (err) {
                setError('Failed to upload image or add banner');
                console.error(err);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleRemoveUploadedImage = async (index, bannerId) => {
        try {
            await apiClient.delete(`/banner/delete/${bannerId}`);
            setBanners(prev => prev.filter((_, i) => i !== index));
        } catch (err) {
            setError('Failed to remove banner');
            console.error(err);
        }
    };

    return (
        <div className="ml-auto mr-auto md:ml-[280px] p-5 mt-10">
            <div className="text-black text-xl font-semibold flex justify-between items-center">
                Banner Management
                <button
                    onClick={handleOpenModal}
                    disabled={loading}
                    className="bg-gradient-to-r from-[#FFBE00] to-[#BA5B00] text-white text-[16px] font-medium rounded-lg px-4 py-2 hover:bg-amber-600 transition duration-200 disabled:opacity-50"
                >
                    Add Banner
                </button>
            </div>

            {error && <div className="text-red-500 mt-2">{error}</div>}
            {loading && <div className="text-gray-500 mt-2">Loading...</div>}

            {/* Display Banners */}
            <div className="flex flex-wrap gap-5 mt-5">
                {banners.map((banner, index) => (
                    <div key={banner.id} className="relative w-[280px]">
                        <img
                            src={banner.image}
                            alt={`Banner ${index + 1}`}
                            className="h-[140px] w-full object-cover rounded"
                        />
                        <button
                            onClick={() => handleRemoveUploadedImage(index, banner.id)}
                            className="absolute top-2 right-2 bg-yellow-500 bg-opacity-70 rounded-full p-1 shadow-md"
                        >
                            <CloseIcon fontSize="small" />
                        </button>
                    </div>
                ))}
            </div>

            <Modal
                open={openModal}
                onClose={handleCloseModal}
                aria-labelledby="modal-title"
                className="flex justify-center items-center"
            >
                <div className="bg-white rounded-2xl w-[90%] max-w-[400px] text-center shadow-2xl p-6 relative">
                    <IconButton
                        onClick={handleCloseModal}
                        className="!absolute top-2 right-2"
                    >
                        <CloseIcon />
                    </IconButton>

                    <h2 className="text-gray-500 font-medium text-lg mb-6">
                        Upload Image Or Video
                    </h2>

                    {!imagePreview ? (
                        <>
                            <input
                                id="menu-upload"
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleImageUpload}
                            />
                            <label htmlFor="menu-upload">
                                <span className="bg-gradient-to-r from-[#FFBE00] to-[#BA5B00] text-white px-6 py-2 rounded-full font-semibold shadow-md cursor-pointer">
                                    Upload
                                </span>
                            </label>
                        </>
                    ) : (
                        <>
                            <div className="relative inline-block mb-5 border-2 border-dotted border-gray-400 rounded-xl p-2">
                                <img
                                    src={imagePreview}
                                    alt="Preview"
                                    className="max-w-full rounded-md"
                                />
                                <button
                                    onClick={removeImage}
                                    className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow"
                                >
                                    <CloseIcon fontSize="small" />
                                </button>
                            </div>

                            <button
                                onClick={handleSubmit}
                                disabled={loading}
                                className="bg-gradient-to-r from-[#FFBE00] to-[#BA5B00] text-white w-full py-2 rounded-full font-semibold shadow-md disabled:opacity-50"
                            >
                                {loading ? 'Uploading...' : 'Submit'}
                            </button>
                        </>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default DataTable;

